import React from "react";

const PrivacyPolicy: React.FC = () => {
  return (
    <div className="max-w-5xl mx-auto px-6 py-12 text-gray-800">
      <h1 className="text-4xl font-bold mb-2">GameBite Privacy Policy</h1>

      <p className="text-gray-600 mb-8">
        <strong>Effective Date:</strong> 26th June 2026
      </p>

      <p className="mb-6">
        GameBite ("we," "our," or "us") respects your privacy and is committed
        to protecting your personal information. This Privacy Policy explains
        how we collect, use, store, and protect information when you use our
        games, applications, websites, and services, including our mobile games
        and Meta Quest (VR) games.
      </p>

      <p className="mb-10">
        By using our games or services, you agree to the collection and use of
        information in accordance with this Privacy Policy.
      </p>

      {/* Information We Collect */}

      <section className="mb-10">
        <h2 className="text-2xl font-semibold mb-4">
          Information We Collect
        </h2>

        <p className="mb-4">
          Depending on the game and platform, we may collect the following
          information.
        </p>

        <h3 className="text-xl font-semibold mt-6 mb-3">
          1. Personal Information
        </h3>

        <p>
          We generally do not require players to provide personal information
          such as:
        </p>

        <ul className="list-disc ml-8 my-4 space-y-2">
          <li>Name</li>
          <li>Home Address</li>
          <li>Email Address</li>
          <li>Phone Number</li>
        </ul>

        <p>Unless you voluntarily provide it by:</p>

        <ul className="list-disc ml-8 my-4 space-y-2">
          <li>Contacting customer support</li>
          <li>Subscribing to newsletters</li>
          <li>Participating in surveys</li>
          <li>Reporting bugs</li>
          <li>Entering competitions</li>
        </ul>

        <h3 className="text-xl font-semibold mt-8 mb-3">
          2. Automatically Collected Information
        </h3>

        <p>
          When you play our games, we may automatically collect technical
          information including:
        </p>

        <ul className="list-disc ml-8 my-4 space-y-2">
          <li>Device model</li>
          <li>Operating system version</li>
          <li>Device language</li>
          <li>Screen resolution</li>
          <li>IP address</li>
          <li>Time zone</li>
          <li>Country or region</li>
          <li>App version</li>
          <li>Unique device identifiers</li>
          <li>Anonymous player identifiers</li>
          <li>Crash reports</li>
          <li>Performance statistics</li>
          <li>Gameplay analytics</li>
        </ul>

        <p>
          This information is used to improve game performance, identify bugs,
          and enhance the player experience.
        </p>

        <h3 className="text-xl font-semibold mt-8 mb-3">
          3. Meta Quest (VR) Information
        </h3>

        <p>
          When you play our games on Meta Quest devices, Meta Platforms may
          collect information according to their own Privacy Policy.
        </p>

        <p className="mt-4">
          Depending on the permissions required by the game, our application may
          receive or process:
        </p>

        <ul className="list-disc ml-8 my-4 space-y-2">
          <li>Meta User ID</li>
          <li>Oculus Platform ID</li>
          <li>Device identifiers</li>
          <li>Multiplayer session information</li>
          <li>Leaderboard information</li>
          <li>Achievement data</li>
          <li>Friend information (when authorized)</li>
          <li>
            Voice communication (only when multiplayer voice chat is enabled)
          </li>
          <li>
            Guardian boundary information (only if required by gameplay)
          </li>
          <li>Motion controller input</li>
          <li>Hand tracking data</li>
          <li>Headset tracking information</li>
          <li>
            Eye tracking or face tracking data (only on supported devices and
            only with your permission)
          </li>
        </ul>

        <p>We do not sell any of these information.</p>

        <p className="mt-4">
          Please refer to Meta's Privacy Policy:
        </p>

        <a
          href="https://www.meta.com/legal/privacy-policy/"
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 underline"
        >
          https://www.meta.com/legal/privacy-policy/
        </a>

        <h3 className="text-xl font-semibold mt-8 mb-3">
          4. Gameplay Information
        </h3>

        <p>To improve gameplay we may collect:</p>

        <ul className="list-disc ml-8 my-4 space-y-2">
          <li>Game progress</li>
          <li>High scores</li>
          <li>Achievements</li>
          <li>Saved settings</li>
          <li>Purchased content</li>
          <li>Session duration</li>
          <li>Levels completed</li>
          <li>In-game currency balances</li>
          <li>Multiplayer statistics</li>
        </ul>

        <h3 className="text-xl font-semibold mt-8 mb-3">5. Purchases</h3>

        <p>
          If you purchase items within our games, all payment transactions are
          processed by the respective platform provider (Google Play, Apple App
          Store, Meta Horizon Store, Steam, etc.).
        </p>

        <p className="mt-4">
          We do not receive or store your payment card information.
        </p>

        <h3 className="text-xl font-semibold mt-8 mb-3">
          6. Third-Party Services
        </h3>

        <p>Our games may use third-party services including:</p>

        <ul className="list-disc ml-8 my-4 space-y-2">
          <li>Unity Services</li>
          <li>Unity Analytics</li>
          <li>Unity Cloud Diagnostics</li>
          <li>Firebase</li>
          <li>Google Play Games Services</li>
          <li>Apple Game Center</li>
          <li>Meta Platform SDK</li>
          <li>Photon Fusion</li>
          <li>PlayFab</li>
          <li>Advertising Networks (if applicable)</li>
        </ul>

        <p>
          These providers may collect information according to their own privacy
          policies.
        </p>
      </section>

      <section className="mb-10">
        <h2 className="text-2xl font-semibold mb-4">
          How We Use Information
        </h2>

        <ul className="list-disc ml-8 space-y-2">
          <li>Operate our games</li>
          <li>Save player progress</li>
          <li>Improve gameplay</li>
          <li>Fix bugs</li>
          <li>Monitor crashes</li>
          <li>Provide multiplayer services</li>
          <li>Prevent cheating</li>
          <li>Provide customer support</li>
          <li>Comply with legal obligations</li>
          <li>Improve game performance</li>
          <li>Develop new features</li>
        </ul>
      </section>

      <section className="mb-10">
        <h2 className="text-2xl font-semibold mb-4">Advertising</h2>

        <p>
          Some of our games may display advertisements. Advertising partners may
          use advertising identifiers to provide personalized or
          non-personalized advertisements in accordance with applicable laws.
        </p>

        <p className="mt-4">
          Players may reset or limit advertising identifiers through their
          device settings.
        </p>
      </section>

      <section className="mb-10">
        <h2 className="text-2xl font-semibold mb-4">Analytics</h2>

        <p>We may use analytics tools to understand:</p>

        <ul className="list-disc ml-8 my-4 space-y-2">
          <li>Player behavior</li>
          <li>Game performance</li>
          <li>Feature usage</li>
          <li>Retention</li>
          <li>Crash frequency</li>
        </ul>

        <p>
          Analytics data is generally collected in an aggregated or anonymized
          form.
        </p>
      </section>

      <section className="mb-10">
        <h2 className="text-2xl font-semibold mb-4">Data Sharing</h2>

        <p>We do not sell your personal information.</p>

        <p className="mt-4">
          We may share limited information with trusted service providers who
          help us operate our games, including:
        </p>

        <ul className="list-disc ml-8 my-4 space-y-2">
          <li>Cloud hosting providers</li>
          <li>Analytics providers</li>
          <li>Multiplayer servers</li>
          <li>Crash reporting services</li>
          <li>Payment processors</li>
          <li>Customer support providers</li>
        </ul>
      </section>

      <section className="mb-10">
        <h2 className="text-2xl font-semibold mb-4">
          Legal Requirements
        </h2>

        <ul className="list-disc ml-8 space-y-2">
          <li>Comply with applicable laws</li>
          <li>Respond to lawful requests</li>
          <li>Protect our legal rights</li>
          <li>Investigate fraud</li>
          <li>Enforce our Terms of Service</li>
          <li>Protect the safety of players</li>
        </ul>
      </section>

      <section className="mb-10">
        <h2 className="text-2xl font-semibold mb-4">Data Retention</h2>

        <p>
          We retain information only for as long as necessary to provide our
          services, comply with legal obligations, resolve disputes, and enforce
          agreements.
        </p>

        <p className="mt-4">
          Inactive player information may be deleted after an extended period of
          inactivity where permitted by applicable law.
        </p>
      </section>

      <section className="mb-10">
        <h2 className="text-2xl font-semibold mb-4">Security</h2>

        <p>
          We use reasonable administrative, technical, and physical safeguards
          to protect your information.
        </p>

        <p className="mt-4">
          However, no method of electronic storage or internet transmission is
          completely secure, and we cannot guarantee absolute security.
        </p>
      </section>

      <section className="mb-10">
        <h2 className="text-2xl font-semibold mb-4">
          Children's Privacy
        </h2>

        <p>
          Our games are not intended for children under the age required by
          applicable law in their country.
        </p>

        <p className="mt-4">
          We do not knowingly collect personal information from children without
          appropriate parental consent.
        </p>

        <p className="mt-4">
          If you believe a child has provided personal information, please
          contact us so we can remove it.
        </p>
      </section>

      <section className="mb-10">
        <h2 className="text-2xl font-semibold mb-4">
          Your Privacy Rights
        </h2>

        <p>
          Depending on your location, you may have rights to:
        </p>

        <ul className="list-disc ml-8 my-4 space-y-2">
          <li>Access your personal data</li>
          <li>Correct inaccurate information</li>
          <li>Request deletion</li>
          <li>Restrict processing</li>
          <li>Object to processing</li>
          <li>Withdraw consent</li>
          <li>Receive a copy of your data (data portability)</li>
        </ul>
      </section>

      <section className="mb-10">
        <h2 className="text-2xl font-semibold mb-4">
          International Transfers
        </h2>

        <p>
          Your information may be processed on servers located outside your
          country of residence.
        </p>

        <p className="mt-4">
          Where required, we implement appropriate safeguards to protect your
          information during international transfers.
        </p>
      </section>

      <section className="mb-10">
        <h2 className="text-2xl font-semibold mb-4">
          Changes to this Privacy Policy
        </h2>

        <p>
          We may update this Privacy Policy from time to time.
        </p>

        <p className="mt-4">
          Any changes will be posted on this page with an updated Effective
          Date.
        </p>

        <p className="mt-4">
          Your continued use of our games after changes become effective
          constitutes acceptance of the updated Privacy Policy.
        </p>
      </section>

      <section>
        <h2 className="text-2xl font-semibold mb-4">Contact Us</h2>

        <p>
          If you have any questions regarding this Privacy Policy or our data
          practices, please contact us:
        </p>

        <div className="mt-4">
          <p className="font-semibold">GameBite</p>
          <p>Email: zehrakanis@gmail.com</p>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPolicy;